import React, { useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import { DataGrid } from '@mui/x-data-grid'
import { DateTime } from 'luxon'
import SettingsIcon from '@mui/icons-material/Settings'
import SearchIcon from '@mui/icons-material/Search'
import EditIcon from '@mui/icons-material/Edit'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RestoreIcon from '@mui/icons-material/Restore'
import BusinessIcon from '@mui/icons-material/Business'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { FC, useCQuery, invalidateQuery } from '../../Services'
import { deleteViaPatch, restoreViaPatch, onCellEditComplete } from './Functions'
import { ConfigDialog } from './modal/ConfigDialog'
import CreateConfigSection from './CreateConfigSection'
import BlacklistSection from './BlacklistSection'

export const Configs = () => {
  const { agencyId } = useParams()
  const navigate = useNavigate()
  const { data: configs = [], isSuccess: isSuccessConfigs } = !agencyId ? useCQuery('configs') : useCQuery(['configs', { agencyId }])

  const [changes, setChanges] = useState({})
  const [configToEdit, setConfigToEdit] = useState(null)
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false)
  const [showDeleted, setShowDeleted] = useState(false)
  const [globalFilterValue, setGlobalFilterValue] = useState('')

  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, config: null })

  const loggedInUser = JSON.parse(window.localStorage.getItem('user')).user
  const isAdmin = loggedInUser.role === 'siapAdmin'

  const handleOpenEdit = useCallback((config) => {
    setConfigToEdit(config)
    setChanges({})
    setIsConfigDialogOpen(true)
  }, [])

  const handleCloseDialog = useCallback(() => {
    setIsConfigDialogOpen(false)
    setConfigToEdit(null)
  }, [])

  const handleSaveChanges = useCallback((newConfig) => {
    setChanges(newConfig)
  }, [])

  const handleEditSave = async () => {
    let isError = false

    if (!changes?.filters) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Nessuna modifica da salvare.' })
      isError = true
    }
    if (!changes?.filters?.length > 0) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Nessun filtro selezionato.' })
      isError = true
    }
    if (changes?.filters?.filter(filter => filter.fileExtensionsToInclude?.length === 0).length > 0) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Nessun file extension selezionato.' })
      isError = true
    }
    if (!changes?.filters?.filter(filter => filter.path?.length !== 0).length > 0) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Nessun path selezionato.' })
      isError = true
    }
    if (changes?.filters?.filter(filter => filter.docType?.length === 0).length > 0) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Nessun tipo di documento selezionato.' })
      isError = true
    }
    if (isError) return

    try {
      const processedChanges = {
        ...changes,
        filters: changes?.filters?.map((filter) => {
          const normalizeSilentBlacklist = (f) => ({
            ...f,
            localSilentBlacklist: Array.isArray(f.localSilentBlacklist)
              ? f.localSilentBlacklist
                .map((e) => {
                  if (typeof e === 'string') return e.trim()
                  if (e && typeof e === 'object' && e.regex != null) return String(e.regex).trim()
                  return ''
                })
                .filter((p) => p.length > 0)
              : []
          })
          if (Object.keys(filter.inclusionRegex).length) {
            const twoSlashesRegex = /.*\/.*\/.*/g
            const string = typeof filter.inclusionRegex === typeof ''
              ? filter.inclusionRegex
              : filter.inclusionRegex?.regex || filter.inclusionRegex?.flags
                ? '/' + (filter.inclusionRegex?.regex || '') + '/' + (filter.inclusionRegex?.flags || '')
                : ''
            const hasTwoOrMoreSlashes = string?.match(twoSlashesRegex)
            if (hasTwoOrMoreSlashes) {
              const slashCount = string.split('/').length - 1
              const flags = string.split('/')[slashCount]
              const processedString = string.substring(1, string.length).replace('/' + flags || '', '')
              return normalizeSilentBlacklist({ ...filter, inclusionRegex: { regex: processedString, flags } })
            }
            return normalizeSilentBlacklist({ ...filter, inclusionRegex: { regex: string, flags: '' } })
          }
          return normalizeSilentBlacklist(filter)
        })
      }

      await FC.service('configs').patch(configToEdit._id, processedChanges)
      invalidateQuery('configs')
      window.growl.show({ severity: 'success', summary: 'Successo', detail: 'Configurazione salvata con successo' })
      handleCloseDialog()
    } catch (e) {
      console.log(e)
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Errore durante il salvataggio del config' })
    }
  }

  const handleConfirmDelete = (config) => {
    setConfirmDialog({ open: true, action: 'delete', config })
  }

  const handleConfirmRestore = (config) => {
    setConfirmDialog({ open: true, action: 'restore', config })
  }

  const handleConfirmAction = async () => {
    const { action, config } = confirmDialog
    if (action === 'delete') await deleteViaPatch(config._id, config.name)
    if (action === 'restore') await restoreViaPatch(config._id)
    setConfirmDialog({ open: false, action: null, config: null })
  }

  const handleCloseConfirm = () => {
    setConfirmDialog({ open: false, action: null, config: null })
  }

  const tableData = showDeleted ? configs.filter(c => c.isDeleted) : configs.filter(c => !c.isDeleted)
  const filteredData = globalFilterValue
    ? tableData.filter(c => Object.values(c).some(v => String(v).toLowerCase().includes(globalFilterValue.toLowerCase())))
    : tableData

  const columns = [
    { field: 'agencyId', headerName: 'Agency ID', flex: 0.8, minWidth: 100 },
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1,
      minWidth: 140,
      editable: isAdmin,
      sortable: true,
      renderCell: isAdmin
        ? (params) => (
            <Stack direction='row' alignItems='center' spacing={0.5} sx={{ width: '100%' }}>
              <span>{params.value ?? '—'}</span>
              <EditOutlinedIcon sx={{ fontSize: 14, color: 'action.active', flexShrink: 0 }} titleAccess='Modificabile' />
            </Stack>
          )
        : undefined
    },
    { field: 'lastEditedBy', headerName: 'Modificato Da', flex: 1, minWidth: 120 },
    {
      field: 'lastEdited', headerName: 'Ultima Modifica', flex: 1, minWidth: 140, sortable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{params.value ? DateTime.fromISO(params.value).toFormat('dd/MM/yyyy HH:mm') : '-'}</Typography>
      )
    },
    {
      field: 'createdAt', headerName: 'Creazione', flex: 1, minWidth: 110, sortable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{params.value ? DateTime.fromISO(params.value).toFormat('dd/MM/yyyy') : '-'}</Typography>
      )
    },
    {
      field: 'clientNum', headerName: 'Client', flex: 0.5, minWidth: 80, sortable: true,
      renderCell: (params) => (
        <Chip label={params.value ?? 0} size='small' variant='outlined' sx={{ fontWeight: 600, minWidth: 32 }} />
      )
    },
    ...(isAdmin
      ? [{
          field: 'actions', headerName: 'Azioni', width: 120, sortable: false, filterable: false,
          renderCell: (params) => (
            <Stack direction='row' spacing={0.5} alignItems='center'>
              <Tooltip title='Modifica configurazione' arrow>
                <IconButton size='small' onClick={() => handleOpenEdit(params.row)} color='primary' aria-label='Modifica'>
                  <EditIcon fontSize='small' />
                </IconButton>
              </Tooltip>
              {showDeleted
                ? (
                  <Tooltip title='Ripristina configurazione' arrow>
                    <IconButton size='small' onClick={() => handleConfirmRestore(params.row)} color='primary' aria-label='Ripristina'>
                      <RestoreIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )
                : (
                  <Tooltip title='Elimina configurazione' arrow>
                    <IconButton size='small' onClick={() => handleConfirmDelete(params.row)} sx={{ color: 'error.main' }} aria-label='Elimina'>
                      <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
            </Stack>
          )
        }]
      : [])
  ]

  if (!isSuccessConfigs) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Stack alignItems='center' spacing={2}>
          <CircularProgress size={40} />
          <Typography variant='body2' color='text.secondary'>Caricamento configurazioni...</Typography>
        </Stack>
      </Box>
    )
  }

  return (
    <Box sx={{ mx: 'auto', width: '94%', maxWidth: 1600, pb: 5 }}>

      {/* Config Dialog */}
      <ConfigDialog
        open={isConfigDialogOpen}
        onClose={handleCloseDialog}
        onConfirmSave={handleEditSave}
        configToEdit={configToEdit}
        onSave={handleSaveChanges}
      />

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCloseConfirm} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WarningAmberIcon color='warning' />
          <Typography variant='h6' component='span' fontWeight={600}>
            {confirmDialog.action === 'delete' ? 'Conferma Eliminazione' : 'Conferma Ripristino'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            {confirmDialog.action === 'delete'
              ? `Vuoi eliminare la configurazione "${confirmDialog.config?.name}"?`
              : `Vuoi ripristinare la configurazione "${confirmDialog.config?.name}"?`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={handleCloseConfirm} variant='outlined' size='small' sx={{ textTransform: 'none' }}>
            Annulla
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant='contained'
            size='small'
            color={confirmDialog.action === 'delete' ? 'error' : 'primary'}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {confirmDialog.action === 'delete' ? 'Elimina' : 'Ripristina'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Page Header */}
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2, mt: 1 }}>
        <Stack direction='row' alignItems='center' spacing={2}>
          <SettingsIcon color='primary' sx={{ fontSize: 28 }} />
          <Typography variant='h5' fontWeight={700} color='text.primary'>
            Gestione Configurazioni
          </Typography>
          <Chip
            label={`${filteredData.length} config`}
            size='small'
            variant='outlined'
            sx={{ fontWeight: 500 }}
          />
        </Stack>

        <Stack direction='row' alignItems='center' spacing={2}>
          {isAdmin && (
            <FormControlLabel
              control={
                <Switch
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                  size='small'
                />
              }
              label={<Typography variant='body2' fontWeight={500}>Mostra Eliminati</Typography>}
            />
          )}
          {isAdmin && (
            <Button
              variant='outlined'
              startIcon={<BusinessIcon />}
              onClick={() => navigate('/siapadmin/agencies')}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              Agenzie
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Blacklist Section */}
      {isAdmin && (
        <Box sx={{ mb: 2 }}>
          <BlacklistSection />
        </Box>
      )}

      {/* Creation Section */}
      {isAdmin && (
        <Box sx={{ mb: 2 }}>
          <CreateConfigSection configs={configs} loggedInUser={loggedInUser} />
        </Box>
      )}

      {/* Configs Table */}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
          <TextField
            size='small'
            value={globalFilterValue}
            onChange={(e) => setGlobalFilterValue(e.target.value)}
            placeholder='Cerca configurazioni...'
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' color='action' />
                  </InputAdornment>
                )
              }
            }}
            sx={{ width: 300 }}
          />
        </Stack>

        <DataGrid
          rows={filteredData}
          columns={columns}
          getRowId={(row) => row._id}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] }
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          processRowUpdate={(newRow, oldRow) => {
            if (newRow.name !== oldRow.name) {
              onCellEditComplete({ rowData: newRow, newValue: newRow.name, field: 'name' }, loggedInUser)
            }
            return newRow
          }}
          sx={{
            height: Math.max(400, Math.min(filteredData.length * 52 + 110, 800)),
            border: 'none',
            '& .MuiDataGrid-cell--editable': {
              cursor: 'pointer',
              backgroundColor: 'rgba(0, 70, 128, 0.06)',
              '&:hover': {
                backgroundColor: 'rgba(0, 70, 128, 0.1)'
              }
            }
          }}
        />
      </Paper>
    </Box>
  )
}
